import { useAppStore } from './store.js';

// Cross-window store sync via BroadcastChannel.
//
// Why: dockview's popout group works because it shares the parent JS
// context. But the moment a user opens hequity in a second browser tab
// (the more common multi-monitor pattern), each tab boots its own
// zustand store backed by its own localStorage — they drift apart.
//
// What we sync: the same slice persist() saves, minus dockview JSON
// (each window owns its own layout geometry). That's tabs, tabOrder,
// activeTabId, watchlist — and crucially, group.currentEntityId AND
// group.selection (the typed home / operator / group / landlord selection),
// so picking anything in one window updates linked panels in the other.
// Both ride inside `tabs[*].groups`, so they're mirrored together as one
// consistent pair — never patch one without the other. Pinned
// `link.selection` and broadcast-only `localSelection` travel the same way
// inside `tabs[*].panels`.
//
// Loop avoidance: each window stamps outgoing messages with a stable
// senderId; incoming messages from the same sender are ignored, and
// messages from other senders apply state under an `applyingRemote`
// flag so the resulting store-change doesn't fan back out.

const CHANNEL_NAME = 'hc.workspace.sync.v1';
const senderId = crypto.randomUUID();

interface SyncMessage {
  senderId: string;
  patch: ReturnType<typeof snapshot>;
}

let applyingRemote = false;

function snapshot() {
  const s = useAppStore.getState();
  return {
    tabs: s.tabs,
    tabOrder: s.tabOrder,
    activeTabId: s.activeTabId,
    watchlist: s.watchlist,
  };
}

let channel: BroadcastChannel | null = null;
let unsubscribe: (() => void) | null = null;

export function startCrossWindowSync(): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    // Older browsers — skip. Persist still works locally.
    return () => undefined;
  }
  if (channel) return () => undefined; // already running

  channel = new BroadcastChannel(CHANNEL_NAME);

  channel.onmessage = (ev: MessageEvent<SyncMessage>) => {
    const msg = ev.data;
    if (!msg || msg.senderId === senderId) return;
    applyingRemote = true;
    try {
      // setState merges shallowly at the top level — matches what
      // persist's rehydrate does. We only ship persisted slices, so
      // actions/etc. on the store aren't touched.
      useAppStore.setState(msg.patch);
    } finally {
      applyingRemote = false;
    }
  };

  // Coalesce rapid mutations into a single broadcast per animation frame.
  let pending = false;
  unsubscribe = useAppStore.subscribe(() => {
    if (applyingRemote) return;
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      channel?.postMessage({ senderId, patch: snapshot() } satisfies SyncMessage);
    });
  });

  return stopCrossWindowSync;
}

export function stopCrossWindowSync(): void {
  unsubscribe?.();
  unsubscribe = null;
  channel?.close();
  channel = null;
}
